// L'EFFACEUR — Garde de mode maintenance
// Logique :
//   1. Au mount + toutes les 60s, GET /maintenance/status
//   2. Si maintenance ACTIVE :
//      - Admin (localStorage.isAdmin === '1') → laisse passer partout
//      - Non-admin → redirige vers /maintenance, sauf si déjà sur /login (pour qu'un admin
//        puisse se logger même en maintenance)
//   3. Si maintenance INACTIVE et user sur /maintenance → renvoie à l'accueil
//
// `isAdmin` est posé en localStorage au login (synchrone, pas d'appel API).
// Permet aux admins de naviguer librement entre /, /dashboard, /admin pendant maintenance.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';

// Routes toujours accessibles (sinon admin ne peut pas se reconnecter en maintenance)
const ALWAYS_ALLOWED = ['/login', '/maintenance', '/forgot-password', '/reset-password', '/auth-callback'];

const MaintenanceGate = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const isAlwaysAllowed = ALWAYS_ALLOWED.some(p =>
      location.pathname === p || location.pathname.startsWith(p + '/')
    );
    if (isAlwaysAllowed) return () => { cancelled = true; };

    // Lecture synchrone du flag admin (posé au login)
    const isAdmin = localStorage.getItem('isAdmin') === '1';
    if (isAdmin) {
      // Admin : pass partout, on revérifie quand même la maintenance pour le cas où elle vient d'être désactivée
      // mais on ne redirige jamais
      return () => { cancelled = true; };
    }

    const checkMaintenance = async () => {
      try {
        const res = await axios.get(`${API}/maintenance/status`);
        if (cancelled) return;
        const data = res.data || {};
        if (data.active) {
          sessionStorage.setItem('maintenance_message', data.message || '');
          sessionStorage.setItem('maintenance_eta', data.eta || '');
          if (location.pathname !== '/maintenance') {
            navigate('/maintenance', { replace: true });
          }
        } else if (location.pathname === '/maintenance') {
          navigate('/', { replace: true });
        }
      } catch {
        // Si l'API ne répond pas, on laisse passer (mieux qu'un site complètement bloqué)
      }
    };

    checkMaintenance();
    const interval = setInterval(checkMaintenance, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Force un rerender quand le storage change (logout/login dans un autre tab)
  useEffect(() => {
    const onStorage = () => setTick(t => t + 1);
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return children;
};

export default MaintenanceGate;
